import type { MetaFunction } from 'react-router'
import { STATIC_PAGE_HEADERS } from "~/lib/cache.server"
import { Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { Shield, Eye, Star, MapPin, Users, Briefcase, Building } from 'lucide-react'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'À propos — AfficheServices' },
  { name: 'description', content: 'Découvrez AfficheServices, la plateforme qui simplifie la mise en relation entre particuliers et artisans vérifiés en France.' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/a-propos' },
]

const VALUES = [
  {
    icon: Shield,
    title: 'Confiance',
    description:
      'Chaque artisan est vérifié et évalué par notre équipe. Nous garantissons des professionnels sérieux, assurés et compétents pour tous vos travaux.',
  },
  {
    icon: Eye,
    title: 'Transparence',
    description:
      'Des devis clairs et détaillés, des avis clients authentiques et un suivi complet de votre projet. Aucune mauvaise surprise.',
  },
  {
    icon: Star,
    title: 'Qualité',
    description:
      'Nous sélectionnons les meilleurs artisans de chaque région et suivons la satisfaction de nos clients pour maintenir un haut niveau de service.',
  },
  {
    icon: MapPin,
    title: 'Proximité',
    description:
      'Trouvez des professionnels proches de chez vous. Notre réseau couvre toute la France pour vous proposer des artisans locaux et réactifs.',
  },
]

const STATS = [
  { value: '100%', label: 'Artisans vérifiés', icon: Users },
  { value: '20+', label: 'Catégories de services', icon: Briefcase },
  { value: '48h', label: 'Délai de réponse', icon: Building },
]

export default function APropos() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="mx-auto max-w-3xl px-4 py-16 lg:px-6">
        {/* Hero */}
        <h1 className="text-3xl font-bold text-slate-900">À propos d'AfficheServices</h1>
        <p className="mt-4 text-sm text-slate-600 leading-relaxed">
          AfficheServices est née d'un constat simple : trouver un artisan fiable et compétent pour
          ses travaux de maison reste un parcours du combattant pour de nombreux Français. Entre les
          devis opaques, les délais non respectés et les professionnels introuvables, les
          particuliers méritent une solution moderne et transparente.
        </p>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
          Fondée en 2024, notre plateforme met en relation les particuliers avec des artisans
          vérifiés dans toute la France. Nous avons bâti un réseau de confiance où chaque
          professionnel est sélectionné pour son sérieux, ses compétences et son engagement qualité.
        </p>

        {/* Mission */}
        <div className="mt-10 border-t border-slate-100 pt-10">
          <h2 className="text-xl font-semibold text-slate-900">Notre mission</h2>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            Simplifier la mise en relation entre particuliers et professionnels du bâtiment en
            France. Nous croyons que chaque propriétaire devrait pouvoir trouver facilement un
            artisan de confiance, obtenir des devis justes et réaliser ses projets en toute
            sérénité.
          </p>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            Pour y parvenir, nous avons développé une plateforme intuitive qui permet de décrire son
            projet en quelques clics et de recevoir jusqu'à 3 devis personnalisés sous 48 heures.
            Notre équipe vérifie chaque professionnel inscrit et suit la satisfaction des clients
            pour garantir un service irréprochable.
          </p>
        </div>

        {/* Timeline */}
        <div className="mt-10 border-t border-slate-100 pt-10">
          <h2 className="text-xl font-semibold text-slate-900">Notre parcours</h2>
          <div className="mt-6 space-y-0">
            {[
              { year: '2024', title: 'Création', desc: 'Fondation d\'AfficheServices à Paris. Première version de la plateforme.', color: 'bg-blue-500' },
              { year: '2025', title: 'Lancement', desc: 'Ouverture au public. Premiers artisans vérifiés et premières demandes de devis.', color: 'bg-emerald-500' },
              { year: "Auj.", title: 'Aujourd\'hui', desc: '20+ catégories de services, couverture nationale. 60+ articles blog, outils de prix et Q&A lancés.', color: 'bg-primary' },
            ].map((item, i) => (
              <div key={item.year} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${item.color} text-xs font-bold text-white`}>{item.year}</div>
                  {i < 2 && <div className="mt-1 h-full w-px bg-slate-200" />}
                </div>
                <div className="pb-8">
                  <h3 className="text-sm font-semibold text-slate-900">{item.title}</h3>
                  <p className="mt-1 text-sm text-slate-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">
          {STATS.map((stat) => (
            <div
              key={stat.label}
              className="rounded-xl border border-slate-100 bg-slate-50 p-5 text-center"
            >
              <stat.icon className="mx-auto h-6 w-6 text-primary" />
              <p className="mt-3 text-2xl font-bold text-slate-900">{stat.value}</p>
              <p className="mt-1 text-xs text-slate-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Values */}
        <div className="mt-10 border-t border-slate-100 pt-10">
          <h2 className="text-xl font-semibold text-slate-900">Nos valeurs</h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            {VALUES.map((value) => (
              <div key={value.title} className="rounded-xl border border-slate-100 p-5">
                <value.icon className="h-6 w-6 text-primary" />
                <h3 className="mt-3 text-lg font-medium text-slate-900">{value.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Team */}
        <div className="mt-10 border-t border-slate-100 pt-10">
          <h2 className="text-xl font-semibold text-slate-900">Notre équipe</h2>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            AfficheServices est portée par une équipe passionnée, réunissant des experts du
            numérique et du secteur du bâtiment. Basée à Paris, notre équipe travaille chaque jour
            pour améliorer l'expérience de nos utilisateurs et développer notre réseau d'artisans
            partenaires.
          </p>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            Nous collaborons étroitement avec les fédérations professionnelles du bâtiment et les
            organismes de certification pour nous assurer que chaque artisan répond aux normes en
            vigueur. Notre objectif : devenir la référence en matière de mise en relation entre
            particuliers et professionnels du bâtiment en France.
          </p>
        </div>

        {/* CTA */}
        <div className="mt-10 border-t border-slate-100 pt-10">
          <div className="rounded-xl bg-primary/5 p-8 text-center">
            <h2 className="text-xl font-semibold text-slate-900">Rejoignez-nous</h2>
            <p className="mx-auto mt-3 max-w-lg text-sm text-slate-600 leading-relaxed">
              Vous êtes artisan et souhaitez développer votre activité ? Rejoignez notre réseau de
              professionnels vérifiés et accédez à des demandes de devis qualifiées chaque mois.
            </p>
            <Link
              to="/register-pro"
              className="mt-6 inline-block rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover hover:shadow-md"
            >
              Inscription professionnelle gratuite
            </Link>
          </div>
        </div>
      </div>

      {/* E-E-A-T authority section */}
      <section className="border-t border-slate-100 bg-white py-14">
        <div className="mx-auto max-w-3xl px-4 lg:px-6">
          <h2 className="text-xl font-bold text-slate-900">AfficheServices en chiffres</h2>
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[
              { value: '20+', label: 'catégories de services' },
              { value: '5 500+', label: 'pages de contenu expert' },
              { value: '20', label: 'villes couvertes' },
              { value: '100%', label: 'artisans vérifiés' },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-center">
                <p className="text-2xl font-bold text-primary">{s.value}</p>
                <p className="mt-1 text-xs text-slate-500">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 space-y-3 text-sm leading-relaxed text-slate-600">
            <p>AfficheServices est né d'un constat simple : trouver un artisan fiable en France est encore trop compliqué. Entre les annuaires obsolètes, les plateformes opaques et le bouche-à-oreille limité, les particuliers manquent d'outils pour comparer objectivement les professionnels.</p>
            <p>Notre mission est de rendre le marché du bâtiment <strong>transparent, accessible et sûr</strong>. Chaque artisan est vérifié (SIRET, assurances, identité), chaque avis est lié à une prestation réelle, et chaque devis est détaillé pour faciliter la comparaison.</p>
            <p>Notre contenu expert — rédigé et vérifié par des professionnels du bâtiment — couvre les prix, les réglementations, les normes et les bonnes pratiques pour chaque métier dans chaque ville de France. Nous nous appuyons sur les données de l'INSEE, de la FFB, de l'ADEME et de France Rénov' pour garantir l'exactitude de nos informations.</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
